import { faBullhorn } from "@fortawesome/free-solid-svg-icons/faBullhorn";
import { faFileExport } from "@fortawesome/free-solid-svg-icons/faFileExport";
import { faRightLeft } from "@fortawesome/free-solid-svg-icons/faRightLeft";
import { faRotateLeft } from "@fortawesome/free-solid-svg-icons/faRotateLeft";
import { faTags } from "@fortawesome/free-solid-svg-icons/faTags";
import { faTrash } from "@fortawesome/free-solid-svg-icons/faTrash";
import { faUserPlus } from "@fortawesome/free-solid-svg-icons/faUserPlus";
import { faXmark } from "@fortawesome/free-solid-svg-icons/faXmark";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import omitBy from "lodash/omitBy";
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Button } from "../../../components/Button";
import { Flex } from "../../../components/Flex";
import { Text } from "../../../components/Text";
import { GetLatestEntriesFilterDto } from "../../../model/entry/dto/GetLatestEntriesFilterDto";
import { EntryKind } from "../../../model/entry/types/EntryKind.enum";
import { UserRoles } from "../../../model/user/types/UserRoles";
import { entrySliceSelectors } from "../../../redux/entry/entry.selector";
import { entryActions } from "../../../redux/entry/entry.slice";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { TestIDs } from "../../../tests/types/testIds";
import { Breakpoints } from "../../../theme/Breakpoints";
import { Rounded } from "../../../theme/Rounded";
import { Spacing } from "../../../theme/Spacing";
import { AtiraToast } from "../../../utils/AtiraToast";
import { getTypedCRUDErrors } from "../../../utils/getTypedCRUDErros";
import { Tooltip } from "../../../utils/local-antd";
import { EntryBulkConvertModal } from "./EntryBulkConvertModal";
import { entryCreateEditDuplicateErrorHandler } from "../utils/entryCreateEditDuplicateErrorHandler";
import { getBulkAvailableKindUpdateOptions } from "../utils/getAvailableKindUpdateOptions";
import { noop } from "lodash";
import { ConvertEntryBulkDto } from "../../../model/entry/dto/ConvertEntryBulkDto";
import { RetrieveEntryBulkDto } from "../../../model/entry/dto/RetrieveEntryBulkDto";
import { useDrawersContext } from "../../tasks/drawers-context";
import { EntryBulkExportModal } from "./EntryBulkExportModal";
import { EntryBulkTagsDropdown } from "./EntryBulkTagsDropdown";
import { TagEntryBulkDto } from "../../../model/entry/dto/TagEntryBulkDto";
import { integrationSliceSelectors } from "../../../redux/integration/integration.selector";
import { WhatsappCampaignLimits } from "../../../model/integration/types/whatsapp/WhatsappCampaignLimits";
import { WACampaignCreateEditDrawer } from "../../integration/components/whatsapp-campaign/WACampaignCreateEditDrawer";
import { CampaignPick } from "../../integration/components/whatsapp-campaign/types/CampaignPick";
import { CURRENT_CAMPAIGN_STATUSES } from "../../integration/components/whatsapp-campaign/utils/campaignStatus";
import { integrationActions } from "../../../redux/integration/integration.slice";
import { campaignWaIdFromPhone } from "../../integration/components/whatsapp-campaign/utils/campaignNumber";
import { getEntryLabel } from "../../../utils/entryUtil";

const Wrapper = styled(Flex)`
  position: relative;
  width: 100%;
  align-items: center;
  gap: ${Spacing.m};
  background-color: ${({ theme }) => theme.darkMain};
  border-radius: ${Rounded.md};
  padding: ${Spacing.s} ${Spacing.m};
  padding-inline-start: ${Spacing.s};
  overflow-x: auto;
  scrollbar-width: thin;
`;

const Selection = styled(Flex)`
  align-items: center;
  gap: ${Spacing.s};
  flex-shrink: 0;
  margin-inline-end: auto;
`;

const Actions = styled(Flex)`
  align-items: center;
  gap: ${Spacing.xs};
`;

// Light on the bar rather than blocks of their own, whichever theme sets it
const ActionButton = styled(Button)`
  && {
    margin: 0;
    height: 2rem;
    padding: 0 ${Spacing.m};
    flex-shrink: 0;
    border-radius: ${Rounded.md};
    color: ${({ theme }) => theme.white};
    background-color: transparent;
  }

  &&:hover:not(:disabled) {
    background-color: rgba(255, 255, 255, 0.14);
  }

  @media (max-width: ${Breakpoints.DESKTOP}) {
    && {
      padding: 0 ${Spacing.s};
    }
  }
`;

const DeleteButton = styled(ActionButton)`
  &&:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.danger};
  }
`;

const ClearButton = styled(ActionButton)`
  && {
    width: 2rem;
    padding: 0;
    border-radius: ${Rounded.circle};
  }
`;

// Narrow screens keep the icons, the tooltips still say what each one does
const ActionLabel = styled.span`
  font-weight: 600;
  white-space: nowrap;

  @media (max-width: ${Breakpoints.DESKTOP}) {
    display: none;
  }
`;

const Divider = styled.span`
  flex-shrink: 0;
  width: 1px;
  height: 1.25rem;
  margin-inline: ${Spacing.xs};
  background-color: rgba(255, 255, 255, 0.25);
`;

const SelectedCount = styled(Text)`
  white-space: nowrap;
  font-weight: bold;
  color: ${({ theme }) => theme.white};
`;

type EntryTableBulkActionsHeaderProps = {};

export const EntryTableBulkActionsHeader: React.FC<
  EntryTableBulkActionsHeaderProps
> = () => {
  const [convertBulkLoading, setConvertBulkLoading] = useState(false);
  const [convertBulkModalVisible, setConvertBulkModalVisible] = useState(false);
  const [convertBulkServerError, setConvertBulkServerError] =
    useState<TypedCRUDError | null>(null);
  const [retrieveBulkLoading, setRetrieveBulkLoading] = useState(false);
  const [campaignPicks, setCampaignPicks] = useState<CampaignPick[]>();

  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { getValues, watch } = useFormContext<GetLatestEntriesFilterDto>();
  const {
    setEntryDeleteVisible,
    setEntryAssignBulkVisible,
    setEntriesToAssign,
    selectedRows: selectedEntriesIds,
    setSelectedRows: setSelectedEntriesIds,
    setDuplicateEntryInitiateResolveVisible,
    setDuplicateEntries,
    setCurrentOnIgnoreAction,
    setDuplicateEntriesGroups,
    entryDeleteLoading,
    setIsBulkEntryAction,
    setBulkEntryExportVisible,
  } = useDrawersContext();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const user = useAppSelector(userSliceSelectors.selectLoggedInUser)!;
  const entriesPage = useAppSelector(
    entrySliceSelectors.selectEntriesTablePage,
  );
  const entriesPageSize = useAppSelector(
    entrySliceSelectors.selectEntriesTablePageSize,
  );
  const entries = useAppSelector(entrySliceSelectors.selectLatestEntries);
  const isMember = useAppSelector(userSliceSelectors.selectIsUserOrgMember);
  const permissions = user.permissions.permissions;

  // Only the current page is loaded, so a selection carried over from another
  // page can't be inspected. If none of it is loaded there's nothing to rule
  // out, and any kind is offered.
  const convertibleKinds = useMemo(() => {
    const selectedKinds = entries
      .filter((entry) => selectedEntriesIds.includes(entry._id))
      .map((entry) => entry.kind);

    return selectedKinds.length
      ? getBulkAvailableKindUpdateOptions(selectedKinds)
      : Object.values(EntryKind);
  }, [entries, selectedEntriesIds]);
  const canConvert = convertibleKinds.length > 0;

  // Deleted entries can only be brought back, so that view has its own action
  // and none of the regular ones.
  const isDeletedView = watch("onlyMarkedForDeletion") === true;

  const canShowAssignButton = user.roles.includes(UserRoles.ORGANIZATION);

  const whatsappMeta = useAppSelector(
    integrationSliceSelectors.selectWhatsAppMetaIntegration,
  );
  // The integrations list only holds connected ones
  const canStartCampaign = Boolean(whatsappMeta) && !isMember;
  const campaignTooBig =
    selectedEntriesIds.length > WhatsappCampaignLimits.MAX_RECIPIENTS;
  const campaigns = useAppSelector(
    integrationSliceSelectors.selectWhatsAppMetaCampaigns,
  );
  const campaignRunning = campaigns.some(({ status }) =>
    CURRENT_CAMPAIGN_STATUSES.includes(status),
  );
  const campaignBlocked = campaignTooBig || campaignRunning;

  const refreshCampaigns = useCallback(() => {
    if (canStartCampaign && whatsappMeta?._id) {
      dispatch(
        integrationActions.getWhatsappMetaCampaigns({
          userId,
          integrationId: whatsappMeta._id,
        }),
      );
    }
  }, [dispatch, canStartCampaign, userId, whatsappMeta?._id]);

  useEffect(refreshCampaigns, [refreshCampaigns]);

  // Only the current page is loaded: an entry picked on another page is
  // known by its id alone, and the API works out its number
  const onStartCampaign = () =>
    setCampaignPicks(
      selectedEntriesIds.map((key) => {
        const id = String(key);
        const entry = entries.find((candidate) => candidate._id === id);

        return {
          kind: "entry",
          id,
          label: entry ? getEntryLabel(entry) : t("common.entry"),
          waId: campaignWaIdFromPhone(entry?.fields?.phone) || undefined,
        };
      }),
    );
  const canExport = !user.roles.includes(UserRoles.MEMBER);
  const hasActionsBeforeDelete =
    canShowAssignButton ||
    permissions.ENTRY.UPDATE ||
    canExport ||
    canStartCampaign;

  const onBulkRetrieve = async () => {
    const dto: RetrieveEntryBulkDto = {
      userId,
      entriesIds: selectedEntriesIds as string[],
    };

    async function action(dto: RetrieveEntryBulkDto) {
      await dispatch(entryActions.retrieveEntryBulk(dto)).unwrap();

      await dispatch(
        entryActions.getLatestEntries({
          userId,
          meta: { count: entriesPageSize, page: entriesPage - 1 },
          ...{
            ...omitBy(getValues(), isNil),
            assigneeIds: getValues().assigneeIds?.filter(Boolean),
          },
        }),
      ).unwrap();

      AtiraToast.success(
        t("entries.table.bulkHeader.retrieve.success", {
          count: selectedEntriesIds.length,
        }),
      );

      setSelectedEntriesIds([]);
    }

    try {
      setRetrieveBulkLoading(true);
      await action(dto);
    } catch (e: any) {
      console.log(e);

      entryCreateEditDuplicateErrorHandler(
        { error: e },
        {
          setEntries: setDuplicateEntries,
          setGroups: setDuplicateEntriesGroups,
          setModal: setDuplicateEntryInitiateResolveVisible,
          setActionModal: noop,
        },
      );
      setCurrentOnIgnoreAction(() => async () => {
        await action({ ...dto, ignoreDuplicates: true });
      });
    } finally {
      setRetrieveBulkLoading(false);
    }
  };

  const onBulkConvert = async (kind: EntryKind) => {
    const dto = {
      userId,
      entriesIds: selectedEntriesIds as string[],
      kind: kind,
    };

    async function action(dto: ConvertEntryBulkDto) {
      await dispatch(entryActions.convertEntryBulk(dto)).unwrap();
      await dispatch(
        entryActions.getLatestEntries({
          userId,
          meta: { count: entriesPageSize, page: entriesPage - 1 },
          ...{
            ...omitBy(getValues(), isNil),
            assigneeIds: getValues().assigneeIds?.filter(Boolean),
          },
        }),
      ).unwrap();
      AtiraToast.success(
        t("entries.table.bulkHeader.convert.success", {
          entries_count: selectedEntriesIds.length,
        }),
      );
      setSelectedEntriesIds([]);

      if (!isEmpty(convertBulkServerError)) {
        setConvertBulkServerError(null);
      }
    }

    try {
      setConvertBulkLoading(true);
      await action(dto);
    } catch (e: any) {
      console.log(e);

      const errors = getTypedCRUDErrors(e);

      if (errors?.length) {
        setConvertBulkServerError(errors[0]);
      }

      entryCreateEditDuplicateErrorHandler(
        { error: e },
        {
          setEntries: setDuplicateEntries,
          setGroups: setDuplicateEntriesGroups,
          setModal: setDuplicateEntryInitiateResolveVisible,
          setActionModal: noop,
        },
      );
      setCurrentOnIgnoreAction(() => async () => {
        await action({ ...dto, ignoreDuplicates: true });
      });
    } finally {
      setConvertBulkLoading(false);
    }
  };

  const onBulkTag = async (change: Pick<TagEntryBulkDto, "add" | "remove">) => {
    try {
      await dispatch(
        entryActions.tagEntryBulk({
          userId,
          entriesIds: selectedEntriesIds as string[],
          ...change,
        }),
      ).unwrap();
      await dispatch(
        entryActions.getLatestEntries({
          userId,
          meta: { count: entriesPageSize, page: entriesPage - 1 },
          ...{
            ...omitBy(getValues(), isNil),
            assigneeIds: getValues().assigneeIds?.filter(Boolean),
          },
        }),
      ).unwrap();
    } catch (e: any) {
      console.log(e);
      AtiraToast.apiError(e);
    }
  };

  const onBulkAssign = async () => {
    setEntriesToAssign(selectedEntriesIds as string[]);
    setEntryAssignBulkVisible(true);
  };

  return (
    <Fragment>
      <Wrapper>
        <Selection>
          <ClearButton
            onClick={() => setSelectedEntriesIds([])}
            icon={faXmark}
            info={t("entries.table.bulkHeader.clear.tooltip")}
            dataTestId={TestIDs.entries.table.bulkHeader.clear}
          />
          <SelectedCount>
            {t("common.n-selected", { number: selectedEntriesIds.length })}
          </SelectedCount>
        </Selection>

        <Actions>
          {isDeletedView && permissions.ENTRY.DELETE ? (
            <ActionButton
              disabled={retrieveBulkLoading}
              onClick={onBulkRetrieve}
              icon={faRotateLeft}
              info={t("entries.table.bulkHeader.retrieve.tooltip")}
            >
              <ActionLabel>{t("common.retrieve")}</ActionLabel>
            </ActionButton>
          ) : null}

          {!isDeletedView && permissions.ENTRY.UPDATE ? (
            <EntryBulkTagsDropdown
              selectedEntriesIds={selectedEntriesIds as string[]}
              onChange={onBulkTag}
            >
              <ActionButton
                icon={faTags}
                info={t("entries.table.bulkHeader.tags.tooltip")}
              >
                <ActionLabel>{t("common.tags")}</ActionLabel>
              </ActionButton>
            </EntryBulkTagsDropdown>
          ) : null}

          {!isDeletedView && canShowAssignButton ? (
            <ActionButton
              onClick={onBulkAssign}
              icon={faUserPlus}
              info={t("entries.table.bulkHeader.assign.tooltip")}
            >
              <ActionLabel>{t("common.assign")}</ActionLabel>
            </ActionButton>
          ) : null}

          {!isDeletedView && permissions.ENTRY.UPDATE ? (
            // A disabled button swallows the pointer events its tooltip needs,
            // so the tooltip sits on a wrapper and the button steps aside.
            <Tooltip
              title={t(
                canConvert
                  ? "entries.table.bulkHeader.convert.tooltip"
                  : "entries.table.bulkHeader.convert.disabled.tooltip",
              )}
            >
              <span>
                <ActionButton
                  disabled={convertBulkLoading || !canConvert}
                  style={canConvert ? undefined : { pointerEvents: "none" }}
                  onClick={() => setConvertBulkModalVisible(true)}
                  icon={faRightLeft}
                >
                  <ActionLabel>{t("common.convert")}</ActionLabel>
                </ActionButton>
              </span>
            </Tooltip>
          ) : null}

          {!isDeletedView && canExport ? (
            <ActionButton
              onClick={() => setBulkEntryExportVisible(true)}
              icon={faFileExport}
              info={t("entries.table.bulkHeader.export.tooltip")}
            >
              <ActionLabel>{t("common.export")}</ActionLabel>
            </ActionButton>
          ) : null}

          {!isDeletedView && canStartCampaign ? (
            <Tooltip
              title={t(
                campaignRunning
                  ? "integration.whatsapp.meta.campaign.oneAtATime"
                  : campaignTooBig
                    ? "entries.table.bulkHeader.campaign.tooMany"
                    : "entries.table.bulkHeader.campaign.tooltip",
                { max: WhatsappCampaignLimits.MAX_RECIPIENTS },
              )}
            >
              <span>
                <ActionButton
                  disabled={campaignBlocked}
                  style={
                    campaignBlocked ? { pointerEvents: "none" } : undefined
                  }
                  onClick={onStartCampaign}
                  icon={faBullhorn}
                >
                  <ActionLabel>
                    {t("entries.table.bulkHeader.campaign.title")}
                  </ActionLabel>
                </ActionButton>
              </span>
            </Tooltip>
          ) : null}

          {/* Set apart at the end, so it's never the first thing under the cursor */}
          {!isDeletedView && permissions.ENTRY.DELETE ? (
            <Fragment>
              {hasActionsBeforeDelete ? <Divider /> : null}
              <DeleteButton
                disabled={entryDeleteLoading}
                onClick={() => {
                  setIsBulkEntryAction(true);
                  setEntryDeleteVisible(true);
                }}
                icon={faTrash}
                info={t("entries.table.bulkHeader.delete.tooltip")}
              >
                <ActionLabel>{t("common.delete")}</ActionLabel>
              </DeleteButton>
            </Fragment>
          ) : null}
        </Actions>
      </Wrapper>

      <EntryBulkConvertModal
        isOpen={convertBulkModalVisible}
        onClose={() => setConvertBulkModalVisible(false)}
        onSubmit={onBulkConvert}
        loading={convertBulkLoading}
        serverError={convertBulkServerError}
        availableKinds={convertibleKinds}
      />

      <EntryBulkExportModal />

      {canStartCampaign ? (
        <WACampaignCreateEditDrawer
          open={Boolean(campaignPicks)}
          onClose={() => setCampaignPicks(undefined)}
          initialPicks={campaignPicks}
          onSaved={refreshCampaigns}
        />
      ) : null}
    </Fragment>
  );
};
